import React, { useCallback, useEffect, useState } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import {
  ShippingZone,
  ShippingRate,
  CarrierConfig,
} from "../../model/SiteShipping";
import ShippingZoneForm from "./ShippingZoneForm";
import ShippingRateForm from "./ShippingRateForm";
import {
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";

const ShippingZoneList: React.FC = () => {
  const [zones, setZones] = useState<ShippingZone[]>([]);
  const [carriers, setCarriers] = useState<CarrierConfig[]>([]);
  const [rates, setRates] = useState<Record<string, ShippingRate[]>>({});
  const [expandedZones, setExpandedZones] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingZone, setEditingZone] = useState<ShippingZone | null>(null);
  const [showZoneForm, setShowZoneForm] = useState(false);

  const [editingRate, setEditingRate] = useState<ShippingRate | null>(null);
  const [rateZoneId, setRateZoneId] = useState<string | null>(null);
  const [showRateForm, setShowRateForm] = useState(false);

  const fetchZones = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [zonesRes, carriersRes] = await Promise.all([
        api.get(API_URLS.SHIPPING_ZONES),
        api.get(API_URLS.SHIPPING_CARRIERS),
      ]);
      setZones(zonesRes.data?.data || zonesRes.data || []);
      setCarriers(carriersRes.data?.data || carriersRes.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao carregar zonas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  const fetchRates = async (zoneId: string) => {
    try {
      const res = await api.get(`${API_URLS.SHIPPING_ZONES}/${zoneId}/rates`);
      setRates((prev) => ({
        ...prev,
        [zoneId]: res.data?.data || res.data || [],
      }));
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao carregar tarifas.");
    }
  };

  const toggleExpand = (zoneId: string) => {
    const next = new Set(expandedZones);
    if (next.has(zoneId)) {
      next.delete(zoneId);
    } else {
      next.add(zoneId);
      if (!rates[zoneId]) fetchRates(zoneId);
    }
    setExpandedZones(next);
  };

  const handleDeleteZone = async (zone: ShippingZone) => {
    if (!confirm(`Eliminar zona "${zone.name}"?`)) return;
    try {
      await api.delete(`${API_URLS.SHIPPING_ZONES}/${zone.id}`);
      await fetchZones();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao eliminar zona.");
    }
  };

  const handleDeleteRate = async (zoneId: string, rateId: string) => {
    if (!confirm("Eliminar esta tarifa?")) return;
    try {
      await api.delete(`${API_URLS.SHIPPING_ZONES}/${zoneId}/rates/${rateId}`);
      await fetchRates(zoneId);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao eliminar tarifa.");
    }
  };

  const handleToggleZoneActive = async (zone: ShippingZone) => {
    try {
      await api.put(`${API_URLS.SHIPPING_ZONES}/${zone.id}`, {
        isActive: !zone.isActive,
      });
      await fetchZones();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao atualizar estado.");
    }
  };

  const carrierName = (id: string) =>
    carriers.find((c) => c.id === id)?.displayName || id.slice(0, 8);

  const formatCents = (cents: number) =>
    (cents / 100).toFixed(2).replace(".", ",") + " EUR";

  if (loading) return <div className="text-gray-500 py-4">A carregar...</div>;

  return (
    <div>
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-2 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Zonas de Envio</h2>
        <button
          className="button is-primary is-small flex items-center gap-1"
          onClick={() => {
            setEditingZone(null);
            setShowZoneForm(true);
          }}
        >
          <Plus className="w-4 h-4" /> Adicionar Zona
        </button>
      </div>

      {zones.length === 0 ? (
        <p className="text-gray-500 text-sm">Nenhuma zona configurada.</p>
      ) : (
        <div className="space-y-2">
          {zones.map((zone) => (
            <div key={zone.id} className="border rounded-lg overflow-hidden">
              {/* Zone header */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 cursor-pointer">
                <div
                  className="flex items-center gap-2 flex-1"
                  onClick={() => toggleExpand(zone.id)}
                >
                  {expandedZones.has(zone.id) ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                  <span className="font-medium text-gray-800">{zone.name}</span>
                  <span className="text-xs text-gray-500">
                    {zone.countries?.join(", ")}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleToggleZoneActive(zone)}
                    title={zone.isActive ? "Desativar" : "Ativar"}
                  >
                    {zone.isActive ? (
                      <ToggleRight className="w-5 h-5 text-green-600" />
                    ) : (
                      <ToggleLeft className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                  <button
                    className="text-blue-600 hover:text-blue-800"
                    title="Editar"
                    onClick={() => {
                      setEditingZone(zone);
                      setShowZoneForm(true);
                    }}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    className="text-red-500 hover:text-red-700"
                    title="Eliminar"
                    onClick={() => handleDeleteZone(zone)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Rates table */}
              {expandedZones.has(zone.id) && (
                <div className="px-4 py-3 border-t">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-600">
                      Tarifas
                    </span>
                    <button
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      onClick={() => {
                        setEditingRate(null);
                        setRateZoneId(zone.id);
                        setShowRateForm(true);
                      }}
                    >
                      <Plus className="w-3 h-3" /> Adicionar Tarifa
                    </button>
                  </div>

                  {!rates[zone.id] ? (
                    <p className="text-gray-400 text-sm">A carregar...</p>
                  ) : rates[zone.id].length === 0 ? (
                    <p className="text-gray-400 text-sm">
                      Sem tarifas configuradas.
                    </p>
                  ) : (
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="py-1 px-2">Transportadora</th>
                          <th className="py-1 px-2">Servico</th>
                          <th className="py-1 px-2">Peso (g)</th>
                          <th className="py-1 px-2">Preco</th>
                          <th className="py-1 px-2">Transito</th>
                          <th className="py-1 px-2">Gratis desde</th>
                          <th className="py-1 px-2 text-right">Acoes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rates[zone.id].map((rate) => (
                          <tr
                            key={rate.id}
                            className="border-b hover:bg-gray-50"
                          >
                            <td className="py-1 px-2">
                              {carrierName(rate.carrierConfigId)}
                            </td>
                            <td className="py-1 px-2">
                              {rate.serviceName || rate.serviceLevel}
                            </td>
                            <td className="py-1 px-2">
                              {rate.minWeightGrams}-
                              {rate.maxWeightGrams || "inf"}
                            </td>
                            <td className="py-1 px-2">
                              {formatCents(rate.priceAmount)}
                            </td>
                            <td className="py-1 px-2">
                              {rate.transitDaysMin}-{rate.transitDaysMax}d
                            </td>
                            <td className="py-1 px-2">
                              {rate.freeShippingThreshold > 0
                                ? formatCents(rate.freeShippingThreshold)
                                : "-"}
                            </td>
                            <td className="py-1 px-2 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Editar"
                                  onClick={() => {
                                    setEditingRate(rate);
                                    setRateZoneId(zone.id);
                                    setShowRateForm(true);
                                  }}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  className="text-red-500 hover:text-red-700"
                                  title="Eliminar"
                                  onClick={() =>
                                    handleDeleteRate(zone.id, rate.id)
                                  }
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showZoneForm && (
        <ShippingZoneForm
          zone={editingZone}
          onClose={(saved) => {
            setShowZoneForm(false);
            setEditingZone(null);
            if (saved) fetchZones();
          }}
        />
      )}

      {showRateForm && rateZoneId && (
        <ShippingRateForm
          zoneId={rateZoneId}
          rate={editingRate}
          carriers={carriers}
          onClose={(saved) => {
            setShowRateForm(false);
            setEditingRate(null);
            if (saved && rateZoneId) fetchRates(rateZoneId);
            setRateZoneId(null);
          }}
        />
      )}
    </div>
  );
};

export default ShippingZoneList;
